import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { useUser } from '../contexts/UserContext';
import { getPublicNoticesUrl } from '../utils/UrlUtilities';
import useLocalStorageLevel from '../contexts/levelHook';

const DISMISS_KEY = 'competition_notice_dismissed';

// In-app destination for a clickable notice (mirrors the Notice Board): result -> that
// competition's scoreboard; ongoing -> the Competition tab at that level.
const noticeTarget = (n) => {
    if (!n.quiz_id) return null;
    const level = encodeURIComponent(n.level || '');
    if (n.notice_type === 'quiz_result') {
        return `/userquiz?view=COMPETITION_QUIZ&level=${level}&quiz=${n.quiz_id}&show=leaderboard`;
    }
    if (n.notice_type === 'new_quiz_ongoing') {
        return `/userquiz?view=COMPETITION_QUIZ&level=${level}`;
    }
    return null;
};

const loadDismissed = () => {
    try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]')); }
    catch (_) { return new Set(); }
};
const persistDismissed = (set) => {
    try { localStorage.setItem(DISMISS_KEY, JSON.stringify([...set])); } catch (_) { /* ignore */ }
};

// Top strip showing competition notices (coming / ongoing / results) for the logged-in user's
// current level. Mirrors EmailVerifyBanner. Dismissal persists per notice id in localStorage.
export default function CompetitionNoticeBanner() {
    const CallApi = useApiCaller();
    const navigate = useNavigate();
    const { userState } = useUser();
    const [level] = useLocalStorageLevel();
    const [notices, setNotices] = useState([]);
    const [dismissed, setDismissed] = useState(loadDismissed);

    // Re-sync dismissals from storage whenever the logged-in user changes. loginState clears the
    // dismissed key on a fresh login, so this makes a re-login re-show previously dismissed
    // notices even though this banner never unmounts.
    useEffect(() => {
        setDismissed(loadDismissed());
    }, [userState?.isLoggedIn, userState?.email]);

    useEffect(() => {
        if (!userState?.isLoggedIn || !level) { setNotices([]); return undefined; }
        let cancelled = false;
        const fetchNotices = async () => {
            // Fetch the plain public-notices feed (no level param); the server already filters to
            // active notices, and we narrow to this user's level client-side just below.
            const res = await CallApi({ url: getPublicNoticesUrl(50), method: 'GET', token: false });
            if (!cancelled && res.success) {
                // Competition notices (linked to a quiz) whose level EXACTLY matches the user's
                // selected level — no global/other-level notices in the top bar.
                setNotices((res.data || []).filter(n => n.quiz_id && n.level === level));
            }
        };
        fetchNotices();
        // Refetch on an interval and when the tab regains focus, so a just-published competition
        // (coming -> ongoing) or a result transition shows up without a manual reload.
        const timer = setInterval(fetchNotices, 30000);
        const onFocus = () => { if (document.visibilityState === 'visible') fetchNotices(); };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onFocus);
        return () => {
            cancelled = true;
            clearInterval(timer);
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onFocus);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState?.isLoggedIn, level]);

    if (!userState?.isLoggedIn) return null;
    const visible = notices.filter(n => !dismissed.has(n.notice_id));
    if (visible.length === 0) return null;

    const dismiss = (id) => {
        const next = new Set(dismissed);
        next.add(id);
        setDismissed(next);
        persistDismissed(next);
    };

    const styleFor = (n) => (n.notice_type === 'quiz_result'
        ? 'alert alert-success'
        : 'alert alert-info');

    return (
        <>
            {visible.map(n => {
                const target = noticeTarget(n);
                return (
                <div
                    key={n.notice_id}
                    className={`${styleFor(n)} d-flex justify-content-between align-items-center mb-0 rounded-0`}
                    role="alert"
                    onClick={target ? () => navigate(target) : undefined}
                    title={target ? 'Open' : undefined}
                    style={{ padding: '0.3rem 0.9rem', fontSize: '0.82rem', lineHeight: 1.25, cursor: target ? 'pointer' : 'default' }}
                >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>
                        <Trophy style={{ width: '0.9rem', height: '0.9rem', marginRight: '0.35rem', verticalAlign: '-2px' }} />
                        <strong>{n.title}</strong>{n.body ? <> — {n.body}</> : null}
                    </span>
                    <button
                        className="btn btn-link p-0 ms-2 flex-shrink-0"
                        style={{ fontSize: '0.95rem', textDecoration: 'none', color: '#dc2626', fontWeight: 700 }}
                        onClick={(e) => { e.stopPropagation(); dismiss(n.notice_id); }}
                        aria-label="Dismiss"
                    >
                        ✕
                    </button>
                </div>
                );
            })}
        </>
    );
}
